/* eslint-disable indent */
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import CustomJoyride from "components/atoms/Joyride";
import CommentSection from "components/molecules/CommentSection";
import ControlsContainer from "components/molecules/ControlsContainer";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import MetadataSection from "components/molecules/MetadataSection";
import ShareModal from "components/molecules/ShareModal";
import StatsSection from "components/molecules/StatsSection";
import { useTutorial } from "components/organisms/TutorialProvider";
import { getCommentsByImage, getMonsterStatsByImageId, getNPCStatsByImageId } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, GameEntityDTO, ICommentDTO, ImageDTO, RequestType, UserPremiumType } from "types";
import { lightboxTutorialSteps } from "utils/joyride/lightboxTutorialSteps";
import * as Styled from "./Lightbox.styled";
interface ImageLightboxWithMetadataProps {
  imageDtos: ImageDTO[];
  initialImageId?: string;
  onClose: () => void;
  open: boolean;
  onToggleVisibility: (imageId: string, newVisibility: boolean) => void;
  onVote: (imageId: string) => void;
  handleConfirmDelete: (imageId?: string) => void;
  onIndexChange: (newIndex: number) => void;
}
export const LightboxWithMetadata: FC<ImageLightboxWithMetadataProps> = ({
  imageDtos,
  initialImageId = "",
  onClose,
  open,
  onToggleVisibility,
  onVote,
  handleConfirmDelete,
  onIndexChange
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(imageDtos.findIndex(dto => dto.id === initialImageId));
  const [imageInfo, setImageInfo] = useState(imageDtos[currentIndex] || null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [comments, setComments] = useState<ICommentDTO[]>([]);
  const [stats, setStats] = useState<GameEntityDTO | null>(null);
  const lastFetchedIdRef = useRef<string | null>(null);
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [runTutorial, setRunTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [imageDimensions, setImageDimensions] = useState({
    width: 300,
    height: 300
  });
  useEffect(() => {
    const img = document.createElement("img");
    img.src = imageInfo?.image || "";
    img.onload = () => {
      setImageDimensions({
        width: img.width,
        height: img.height
      });
    };
  }, [imageInfo?.image]);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (open &&
    // Assuming modalOpen indicates the Lightbox is open
    tutorialState.controlsContainer.completed && !tutorialState.lightbox.completed && !tutorialState.lightbox.started) {
      startTutorial("lightbox");
    }
  }, [open, tutorialState, startTutorial, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.lightbox.started && !tutorialState.lightbox.completed) {
      setRunTutorial(true);
    }
  }, [tutorialState]);
  const handleTutorialCallback = (data: {
    status: any;
    type: any;
    action: any;
  }) => {
    const {
      status,
      type,
      action
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      completeTutorial("lightbox");
      setRunTutorial(false);
      setTutorialStepIndex(0);
    } else if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setTutorialStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setTutorialStepIndex(prev => prev - 1);
      }
    }
  };
  const handleUpscalerModalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
      toast.error("This image has already been upscaled and cannot be upscaled again.");
    } else {
      if (imageInfo) {
        router.push({
          pathname: "/edit",
          query: {
            editMode: "Upscale Image",
            imageId: imageInfo.id
          }
        });
      }
    }
  };
  const handleInpaintClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Spot Heal",
          imageId: imageInfo.id
        }
      });
    }
  };
  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShareModalOpen(true);
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const handleCompetitionsClick = () => {
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleEditClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Spot Heal",
          imageId: imageInfo.id
        }
      });
    }
  };
  const handleBGRemovalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Remove Background",
          imageId: imageInfo.id
        }
      });
    }
  };
  const handleADetailerClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo) {
      router.push({
        pathname: "/edit",
        query: {
          imageId: imageInfo.id
        }
      });
    }
  };
  const handleFaceSwapClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Face Swap",
          imageId: imageInfo.id
        }
      });
    }
  };
  const handleAudioClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Apply Audio",
          imageId: imageInfo.id
        }
      });
    }
  };
  const moveToNextImage = useCallback(() => {
    if (!isShareModalOpen) {
      setCurrentIndex(prevIndex => {
        const newIndex = (prevIndex + 1) % imageDtos.length;
        onIndexChange(newIndex);
        return newIndex;
      });
    }
  }, [imageDtos.length, onIndexChange, isShareModalOpen]);
  const moveToPreviousImage = useCallback(() => {
    if (!isShareModalOpen) {
      setCurrentIndex(prevIndex => {
        const newIndex = (prevIndex - 1 + imageDtos.length) % imageDtos.length;
        onIndexChange(newIndex); // Notify the parent component
        return newIndex;
      });
    }
  }, [imageDtos.length, onIndexChange, isShareModalOpen]);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const handleModalRef = (node: HTMLDivElement | null) => {
    modalRef.current = node;
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!modalRef.current || !open) return;
      switch (event.key) {
        case "ArrowRight":
          event.stopPropagation();
          moveToNextImage?.();
          break;
        case "ArrowLeft":
          event.stopPropagation();
          moveToPreviousImage?.();
          break;
      }
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, moveToPreviousImage, moveToNextImage]);
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  useEffect(() => {
    const newIndex = imageDtos.findIndex(dto => dto.id === initialImageId);
    setCurrentIndex(newIndex);
    setImageInfo(imageDtos[newIndex] || null);
  }, [imageDtos, initialImageId]);
  useEffect(() => {
    setImageInfo(imageDtos[currentIndex]);
  }, [currentIndex, imageDtos]);
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = imageDtos.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.votedByUser;
    try {
      await onVote(imageId);
      imageDtos.map(img => {
        if (img.id === imageId) {
          return {
            ...img,
            votedByUser: newVoteStatus,
            voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
          };
        }
        return img;
      });
      if (imageInfo.id === imageId) {
        setImageInfo(prev => ({
          ...prev,
          votedByUser: newVoteStatus,
          voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
        }));
      }
      const newIndex = imageDtos.findIndex(img => img.id === imageId);
      setCurrentIndex(newIndex);
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const confirmDelete = async () => {
    const currentIndex = imageDtos.findIndex(img => img.id === imageToDelete);
    if (imageToDelete) {
      handleConfirmDelete(imageToDelete);
    } else {
      handleConfirmDelete();
    }
    if (imageDtos.length > 1) {
      if (currentIndex >= imageDtos.length - 1) {
        moveToPreviousImage();
      }
      moveToNextImage();
    } else {
      onClose();
    }
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const [toggles, setToggles] = useState<{
    [key: string]: boolean;
  }>(imageDtos.reduce((acc, image) => ({
    ...acc,
    [image.id]: image.public
  }), {}));
  useEffect(() => {
    setToggles(imageDtos.reduce((acc, image) => ({
      ...acc,
      [image.id]: image.public
    }), {}));
  }, [imageDtos]);
  const handleToggle = async (imageId: string) => {
    const currentVisibility = toggles[imageId];
    const newVisibility = !currentVisibility;
    try {
      await onToggleVisibility(imageId, newVisibility);
      setToggles(prev => ({
        ...prev,
        [imageId]: newVisibility
      }));
    } catch (error: any) {
      captureException("Failed to update visibility status.", error);
    }
  };
  useEffect(() => {
    if (imageInfo) {
      const fetchComments = async () => {
        const response = await getCommentsByImage(imageInfo.id);
        if (response.comments) {
          setComments(response.comments);
        } else if (response.error) {
          console.error("Failed to fetch comments:", response.error);
        }
      };
      fetchComments();
    }
  }, [imageInfo]);
  useEffect(() => {
    let isMounted = true; // Flag to track if the component is mounted

    const fetchStats = async () => {
      if (!imageInfo) return;
      if (imageInfo.id !== lastFetchedIdRef.current) {
        let response;
        switch (imageInfo.imageMetadata.requestType) {
          case RequestType.MONSTER:
            response = await getMonsterStatsByImageId(imageInfo.id);
            break;
          case RequestType.NPC:
            response = await getNPCStatsByImageId(imageInfo.id);
            break;
          default:
            return;
        }
        if (!response) {
          if (isMounted) setStats(null);
        } else {
          if (isMounted) setStats(response);
        }
        lastFetchedIdRef.current = imageInfo.id;
      }
    };
    fetchStats();
    return () => {
      isMounted = false;
    };
  }, [imageInfo]);
  const renderMetadata = () => <MetadataSection imageInfo={imageInfo} data-sentry-element="MetadataSection" data-sentry-component="renderMetadata" data-sentry-source-file="index.tsx" />;
  const renderComments = () => <CommentSection image={imageInfo} initialComments={comments} data-sentry-element="CommentSection" data-sentry-component="renderComments" data-sentry-source-file="index.tsx" />;
  const renderStats = useCallback(() => {
    return <StatsSection image={imageInfo} statsProps={stats} />;
  }, [imageInfo, stats]);
  const isVideo = imageInfo?.image.endsWith(".mp4");
  const isAudio = imageInfo?.image.endsWith(".mp3");
  const aspectRatio = isVideo ? parseFloat(imageInfo.imageMetadata.npcFieldsMap["Size"]?.split(":")[0] || "1") / parseFloat(imageInfo.imageMetadata.npcFieldsMap["Size"]?.split(":")[1]) : imageDimensions.width && imageDimensions.height ? imageDimensions.width / imageDimensions.height : 1;
  return <>
            {runTutorial && open && <CustomJoyride steps={lightboxTutorialSteps} run={runTutorial} stepIndex={tutorialStepIndex} disableScrolling={false} callback={handleTutorialCallback} />}
            <Dialog open={open} onOpenChange={onClose} data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
                <DialogContent ref={handleModalRef} className="max-w-8xl w-full z-[1001] p-[0] gap-0" aria-describedby="Lightbox" style={{
        maxHeight: "95vh"
      }} /* Add this */ data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                    <Styled.StyledModal data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {/* Image Section */}
                        <Styled.ImageSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.ImageWrapper $aspectRatio={aspectRatio} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                {imageInfo && isVideo ? <ReactPlayer url={imageInfo.image} width="100%" height="100%" playing controls={true} loop muted playsinline /> : isAudio ? imageInfo && <div className="w-full h-full flex items-center justify-center">
                                            <ReactAudioPlayer src={imageInfo.image} autoPlay controls loop />
                                        </div> : imageInfo && <Link href={imageInfo?.image} passHref>
                                            <Styled.StyledImage src={imageInfo?.image} alt="Artwork" width={imageDimensions.width} height={imageDimensions.height} layout="responsive" loading="eager" />
                                        </Link>}
                                {/* Navigation Buttons */}
                                <Styled.NavButton className="left" onClick={e => {
                e.stopPropagation();
                moveToPreviousImage?.();
              }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <ChevronLeft height={24} width={24} data-sentry-element="ChevronLeft" data-sentry-source-file="index.tsx" />
                                </Styled.NavButton>
                                <Styled.NavButton className="right" onClick={e => {
                e.stopPropagation();
                moveToNextImage?.();
              }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <ChevronRight height={24} width={24} data-sentry-element="ChevronRight" data-sentry-source-file="index.tsx" />
                                </Styled.NavButton>
                            </Styled.ImageWrapper>
                            {/* Controls Container */}
                            <Styled.ControlsWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                {imageInfo && <ControlsContainer imageInfo={imageInfo} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} handleAudioClick={() => handleAudioClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} showLabel={true} />}
                            </Styled.ControlsWrapper>
                        </Styled.ImageSection>
                        {/* Metadata Section */}
                        <Styled.MetadataSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {renderMetadata()}
                            {renderStats()}
                            {renderComments()}
                        </Styled.MetadataSection>
                    </Styled.StyledModal>
                </DialogContent>
                {imageInfo && <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={imageInfo.id} />}
                <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={confirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
                {imageInfo && <ShareModal isOpen={isShareModalOpen} image={imageInfo} onClose={handleCloseShareModal} />}
            </Dialog>
        </>;
};